import React, { useState, useEffect } from "react";
import { getAuthToken, getUserProfile, getSourceName } from "../services/authService";
import {
  getApiBaseUrl,
  PORTAL_BASE_URL,
  withTrailingSlash,
} from "../config/urls";

// Resolve source_name from the live session (set from login response).
const getSessionSourceName = () =>
  getSourceName() || getUserProfile()?.client_source_name || "";
const getSessionEmail = () => getUserProfile()?.email || "";

const AddCommentModal = ({
  isOpen,
  onClose,
  onSubmit,
  caseData,
  partName,
  selectedAction,
  partData,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setComment(""); // Reset comment when modal opens
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  const handleSubmit = async () => {
    // Allow submission with or without comment

    try {
      const token = getAuthToken();
      if (!token) {
        console.error("No authentication token available");
        return;
      }

      // Helper function to get intensity based on action
      const getIntensityByAction = (action) => {
        switch (action) {
          case "No Damage":
            return 0;
          case "Repair":
            return 8;
          case "Replace":
            return 10;
          default:
            return partData?.Intensity || partData?.intensity || 0;
        }
      };

      // Helper function to get the part ID from various possible fields
      const getPartId = (part) => {
        return (
          part?.part_id ||
          part?.partId ||
          part?.id ||
          part?.Part_ID ||
          part?.partID ||
          part?.part_ids?.[0] || // Get first part ID from array
          ""
        );
      };

      // Helper function to get the first available image from part data
      const getPartImage = (part) => {
        // Prioritize damage images first, then part images
        const damageImage =
          part?.damage_images?.[0]?.upload_picture ||
          part?.damage_images?.[0]?.picture;
        const partImage =
          part?.part_images?.[0]?.upload_picture ||
          part?.part_images?.[0]?.picture;

        return damageImage || partImage || "";
      };

      // Prepare the first API data (assess_update_review_history)
      const partId = getPartId(partData);
      const partImage = getPartImage(partData);

      // Convert partId to number for API call
      const partIdAsNumber = partId ? Number(partId) : "";

      const userEmail =
        getSessionEmail();

      const apiData = {
        data: [
          {
            is_fp: false,
            image: partImage,
            intensity: getIntensityByAction(selectedAction),
            action: selectedAction || "No Damage",
            part_id: partIdAsNumber,
            part_name: partName || partData?.part_name || "",
            partdamage_id: "",
            ref_num: caseData?.info?.ref_num || "",
            source_name: getSessionSourceName(),
            vehicle_id: caseData?.info?.id || "",
            edited_part_coords: "{}",
            user_id: userEmail,
            user_type: 10,
          },
        ],
        hello: "world",
        reviewerComment: {
          comment: comment.trim(),
          ref_num: caseData?.info?.ref_num || "",
          source_name: getSessionSourceName(),
          vehicle_id: caseData?.info?.id || "",
          user_id: userEmail,
          user_type: 10,
        },
        verified_list: [],
      };

      // Prepare the second API data (vehicle update)
      const vehicleUpdateData = new URLSearchParams({
        keyValue: JSON.stringify({ claim_state: 5 }),
        ref_num: caseData?.info?.ref_num || "",
        source_name: getSessionSourceName(),
        user_id: userEmail,
      });

      // Make both API calls in parallel
      const [reviewResponse, vehicleResponse] = await Promise.all([
        // First API call
        fetch(`${getApiBaseUrl()}/assess_update_review_history`, {
          method: "POST",
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Origin: PORTAL_BASE_URL,
            Referer: withTrailingSlash(PORTAL_BASE_URL),
          },
          body: `data=${encodeURIComponent(JSON.stringify(apiData))}`,
        }),

        // Second API call
        fetch(`${getApiBaseUrl()}/vehicle/${caseData?.info?.id}/`, {
          method: "PUT",
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Origin: PORTAL_BASE_URL,
            Referer: withTrailingSlash(PORTAL_BASE_URL),
          },
          body: vehicleUpdateData.toString(),
        }),
      ]);

      // Check if both requests were successful
      const reviewSuccess = reviewResponse.ok;
      const vehicleSuccess = vehicleResponse.ok;

      if (reviewSuccess && vehicleSuccess) {
        console.log("Both APIs called successfully");
      } else {
        if (!reviewSuccess) {
          console.error(
            "Failed to update review history:",
            reviewResponse.statusText,
          );
        }
        if (!vehicleSuccess) {
          console.error(
            "Failed to update vehicle:",
            vehicleResponse.statusText,
          );
        }
      }

      // Always proceed with UI flow regardless of API success/failure
      onSubmit(comment.trim());
      handleClose();
    } catch (error) {
      console.error("Error calling APIs:", error);
      // Still call onSubmit to allow the UI to proceed
      onSubmit(comment.trim());
      handleClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSubmit();
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  if (!isOpen) return null;

  // Get case ID from caseData
  const caseId =
    caseData?.info?.num_plate ||
    caseData?.fullCaseId ||
    caseData?.caseId ||
    "N/A";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-[24px] w-full max-w-[608px] h-auto max-h-[85vh] overflow-y-auto relative transition-all duration-300 ease-out border border-gray-200 shadow-2xl lg:h-[530px] lg:overflow-hidden ${
          isAnimating
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 translate-y-4"
        }`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2
            className="text-text-primary text-base font-semibold leading-normal mb-4"
            style={{
              fontFamily: '"Noto Sans", sans-serif',
              fontWeight: 600,
              letterSpacing: "-0.16px",
            }}
          >
            Confirm Changes and Click on Save
          </h2>
        </div>

        {/* Case ID, Part Name and Action Section */}
        <div className="px-6 mb-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Inspection ID */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <label
                className="block text-text-secondary text-xs font-medium mb-2 uppercase tracking-[-0.12px]"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 500,
                }}
              >
                Inspection ID
              </label>
              <span
                className="text-text-primary text-base font-semibold"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 600,
                  letterSpacing: "-0.16px",
                }}
              >
                {caseId}
              </span>
            </div>

            {/* Part Name */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <label
                className="block text-text-secondary text-xs font-medium mb-2 uppercase tracking-[-0.12px]"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 500,
                }}
              >
                Part Name
              </label>
              <span
                className="text-text-primary text-base font-semibold"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 600,
                  letterSpacing: "-0.16px",
                }}
              >
                {partName || caseData?.part_name || "Unknown Part"}
              </span>
            </div>

            {/* Action */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <label
                className="block text-text-secondary text-xs font-medium mb-2 uppercase tracking-[-0.12px]"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 500,
                }}
              >
                Action
              </label>
              <span
                className="text-text-primary text-base font-semibold"
                style={{
                  fontFamily: '"Noto Sans", sans-serif',
                  fontWeight: 600,
                  letterSpacing: "-0.16px",
                }}
              >
                {selectedAction || "No action selected"}
              </span>
            </div>
          </div>
        </div>

        {/* Note Section */}
        <div className="px-6 mb-0">
          <label
            className="block text-text-secondary text-xs font-medium mb-3 uppercase tracking-[-0.12px]"
            style={{
              fontFamily: '"Noto Sans", sans-serif',
              fontWeight: 500,
            }}
          >
            NOTE (OPTIONAL)
          </label>
          <div className="relative">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="You can add a note for this change"
              className="w-full h-[200px] bg-white border border-border-primary rounded text-text-primary placeholder-[#7e848c] p-4 resize-none focus:outline-none focus:border-[#1e2125] transition-colors"
              style={{
                fontFamily: '"Noto Sans", sans-serif',
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
              }}
              autoFocus
            />
            {comment.length === 0 && (
              <div className="absolute top-4 left-4 pointer-events-none">
                <div className="flex items-center gap-1">
                  <span className="text-text-primary">|</span>
                  {/* <span
                    className="text-text-secondary text-xs font-medium"
                    style={{
                      fontFamily: '"Noto Sans", sans-serif',
                      fontWeight: 500,
                      letterSpacing: "-0.12px",
                    }}
                  >
                    Add note on this case
                  </span> */}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit and Cancel Buttons */}
        <div className="px-6 pb-8 pt-6">
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-0 py-3 rounded-[32px] transition-all bg-white border border-border-primary text-text-secondary hover:bg-gray-50"
              style={{
                fontFamily: '"Noto Sans", sans-serif',
                fontWeight: 600,
                fontSize: "16px",
                letterSpacing: "-0.16px",
              }}
            >
              CANCEL
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 px-0 py-3 rounded-[32px] transition-all bg-text-primary text-white hover:bg-[#2a2a2a]"
              style={{
                fontFamily: '"Noto Sans", sans-serif',
                fontWeight: 600,
                fontSize: "16px",
                letterSpacing: "-0.16px",
              }}
            >
              SAVE
            </button>
          </div>
        </div>

        {/* Keyboard shortcuts hint */}
        {/* <div className="absolute top-4 right-4">
          <span className="text-text-secondary text-xs">
            Ctrl+Enter to submit • Esc to close
          </span>
        </div> */}
      </div>
    </div>
  );
};

export default AddCommentModal;
